import { nanoid } from 'nanoid';
import { ICreateNodePayload } from '../editor-block/store/types/actions';
import { IContentMutationPayload } from '@/editor/editor-block/store/types/mutations';
import { INodePayload, IRowPayload, ICellPayload } from '@/editor/types/models-update';
import {
  ICell,
  IEditorBlock,
  INode,
  INodeBase,
  IRow,
  IBlockWidth,
  ISection,
  ITableStyle,
  IImage,
  IExtractNodeIds,
} from '@/editor/types';
import {
  EBlockWidth,
  EBreakpoint,
  EDividerPadding,
  EDividerType,
  EImageAlign,
  EImageSize,
  ENodeType,
  ERowType,
} from '@/editor/types/enums';
import { fallbackTableContent, fallbackTextContent } from '@/editor/editor-block/constants';
import { IEditorContentState } from '@/editor/editor-block/store/editor-content.types';

export class EditorHelper {
  static hasHiddenContentRight({ offsetWidth, scrollWidth, scrollLeft }: HTMLElement): boolean {
    if (scrollWidth <= offsetWidth) {
      return false;
    }

    return offsetWidth + scrollLeft !== scrollWidth;
  }

  static isBlockWidth(size: EBlockWidth, width: number): boolean {
    switch (size) {
      case EBlockWidth.XXSmall:
        return width <= EBreakpoint.XXSmall;
      case EBlockWidth.XSmall:
        return width <= EBreakpoint.XSmall;
      case EBlockWidth.Small:
        return width <= EBreakpoint.Small;
      case EBlockWidth.Medium:
        return width <= EBreakpoint.Medium;
      case EBlockWidth.Large:
        return width <= EBreakpoint.Large;
      case EBlockWidth.XLarge:
        return width > EBreakpoint.Large;
    }
  }

  static getBlockWidth(width: number): IBlockWidth {
    return {
      xxs: EditorHelper.isBlockWidth(EBlockWidth.XXSmall, width),
      xs: EditorHelper.isBlockWidth(EBlockWidth.XSmall, width),
      sm: EditorHelper.isBlockWidth(EBlockWidth.Small, width),
      md: EditorHelper.isBlockWidth(EBlockWidth.Medium, width),
      lg: EditorHelper.isBlockWidth(EBlockWidth.Large, width),
      xl: EditorHelper.isBlockWidth(EBlockWidth.XLarge, width),
    };
  }

  static getBlockWidthClass(blockWidth: IBlockWidth): EBlockWidth {
    if (blockWidth?.xxs) {
      return EBlockWidth.XXSmall;
    } else if (blockWidth?.xs) {
      return EBlockWidth.XSmall;
    } else if (blockWidth?.sm) {
      return EBlockWidth.Small;
    } else if (blockWidth?.md) {
      return EBlockWidth.Medium;
    } else if (blockWidth?.lg) {
      return EBlockWidth.Large;
    } else if (blockWidth?.xl) {
      return EBlockWidth.XLarge;
    } else {
      return EBlockWidth.XXSmall;
    }
  }

  static getNewRow({ rowId: id, rowType: type, cells }: IRowPayload): IRow {
    return {
      id,
      type,
      cells: cells.map((cell: ICellPayload) => ({
        ...cell,
        id: cell.cellId,
        nodes: cell.nodes.map(EditorHelper.getNewNode),
      })),
    };
  }

  static getNewNode(payload: INodePayload): INode {
    const {
      tableStyle,
      dividerType,
      dividerPadding,
      imageSize,
      caption,
      heading,
      content,
      imageId,
      srcUrl,
      linkUrl,
      fields,
      imageAlign,
      nodeType: type,
      nodeId: id,
    } = payload;

    const baseNode: INodeBase = {
      id,
      type,
    };

    switch (type) {
      case ENodeType.Text:
        return {
          ...baseNode,
          content: content || fallbackTextContent,
          fields: fields || [],
        };

      case ENodeType.Image:
        return {
          ...baseNode,
          imageId: imageId || '',
          srcUrl: srcUrl || '',
          linkUrl: linkUrl || '',
          imageSize: imageSize || EImageSize.Original,
          caption: caption || '',
          heading: heading || '',
          imageAlign: imageAlign || EImageAlign.Center,
        };

      case ENodeType.Table:
        return {
          ...baseNode,
          content: content || fallbackTableContent,
          tableStyle: tableStyle || ({} as ITableStyle),
          fields: fields || [],
        };

      case ENodeType.Divider:
        return {
          ...baseNode,
          dividerType: dividerType || EDividerType.Thin,
          dividerPadding: dividerPadding || EDividerPadding.None,
        };

      default:
        return undefined;
    }
  }

  static mapBlock(block: IEditorBlock, payload: IContentMutationPayload): IEditorBlock {
    if (block.contentId === payload.contentId) {
      return {
        ...block,
        content: {
          ...block.content,
          sections: block.content.sections.map((section: ISection) =>
            section.id === payload.targetSectionId
              ? payload.updateSection(section, payload)
              : section
          ),
        },
      };
    }
    return block;
  }

  static addRow(
    section: ISection,
    { targetRowId, row, appendRow }: IContentMutationPayload
  ): ISection {
    const index: number = section.rows.findIndex(({ id }: IRow) => id === targetRowId);

    if (index === -1) {
      return {
        ...section,
        rows: [...section.rows, row],
      };
    }

    return {
      ...section,
      ...section.rows.splice(index + Number(!!appendRow), 0, row),
    };
  }

  static deleteCell(row: IRow, { targetCellId, targetRowId }: IContentMutationPayload): IRow {
    return row.id === targetRowId
      ? {
          ...row,
          cells: row.cells.filter(({ id }: ICell) => id !== targetCellId),
        }
      : row;
  }

  static deleteRow(section: ISection, { targetRowId }: IContentMutationPayload): ISection {
    return {
      ...section,
      rows: section.rows.filter(({ id }: IRow) => id !== targetRowId),
    };
  }

  static mapSection(section: ISection, payload: IContentMutationPayload): ISection {
    return {
      ...section,
      rows: section.rows.map((row: IRow) => payload.updateRow(row, payload)),
    };
  }

  static mapRow(row: IRow, payload: IContentMutationPayload): IRow {
    if (row.id === payload.targetRowId) {
      return {
        ...row,
        cells: row.cells.map((cell: ICell) => payload.updateCell(cell, payload)),
      };
    }
    return row;
  }

  static addCell(
    row: IRow,
    { targetRowId, targetCellId, cell, appendCell }: IContentMutationPayload
  ): IRow {
    if (row.id !== targetRowId) {
      return row;
    }

    const index: number = row.cells.findIndex(({ id }: ICell) => id === targetCellId);

    if (index === -1) {
      return {
        ...row,
        cells: [...row.cells, cell],
      };
    }

    return {
      ...row,
      ...row.cells.splice(index + Number(!!appendCell), 0, cell),
    };
  }

  static updateCell(row: IRow, payload: IContentMutationPayload): IRow {
    return {
      ...row,
      cells: row.cells.map((cell: ICell) =>
        cell.id === payload.targetCellId
          ? { ...cell, ...payload.cellUpdate, nodes: cell.nodes }
          : cell
      ),
    };
  }

  static mapCell(cell: ICell, payload: IContentMutationPayload): ICell {
    return cell.id === payload.targetCellId ? payload.updateNode(cell, payload) : cell;
  }

  static addNodes(
    cell: ICell,
    { targetNodeId, nodes, appendNode }: IContentMutationPayload
  ): ICell {
    const index: number = cell.nodes.findIndex(({ id }: INode) => id === targetNodeId);

    if (index === -1) {
      return {
        ...cell,
        nodes: [...cell.nodes, ...nodes],
      };
    }

    return {
      ...cell,
      ...cell.nodes.splice(index + Number(!!appendNode), 0, ...nodes),
    };
  }

  static deleteNodes(cell: ICell, { targetNodeIds }: IContentMutationPayload): ICell {
    return {
      ...cell,
      nodes: cell.nodes.filter(({ id }: INode) => !targetNodeIds.includes(id)),
    };
  }

  static getBlockById({ blocks }: IEditorContentState, id: string): IEditorBlock {
    return this.deepCopy(blocks.find(({ contentId }: IEditorBlock) => contentId === id));
  }

  static getRowPayload(type: ERowType, payload?: ICreateNodePayload): IRowPayload {
    if (type === ERowType.SideBySide) {
      return {
        rowId: nanoid(),
        rowType: ERowType.SideBySide,
        cells: [
          {
            cellId: nanoid(),
            width: 50,
            nodes: [{ nodeId: nanoid(), nodeType: ENodeType.Image }],
          },
          {
            cellId: nanoid(),
            width: 50,
            nodes: [{ nodeId: nanoid(), nodeType: ENodeType.Text }],
          },
        ],
      };
    } else {
      if (!payload) {
        return {
          rowId: nanoid(),
          rowType: ERowType.Standard,
          cells: [{ cellId: nanoid(), width: 100, nodes: [] }],
        };
      }

      const { nodeType, ...rest }: ICreateNodePayload = payload;
      const nodes = [{ nodeId: nanoid(), nodeType, ...rest }];

      return {
        rowId: nanoid(),
        rowType: ERowType.Standard,
        cells: [{ cellId: nanoid(), width: 100, nodes }],
      };
    }
  }

  static getNodeIdsToMove(
    { blocks }: IEditorContentState,
    {
      contentId,
      targetSectionId,
      targetRowId,
      targetCellId,
      targetNodeId,
      append = true,
    }: IExtractNodeIds
  ): string[] {
    const cell: ICell = blocks
      .find(({ contentId: id }: IEditorBlock) => id === contentId)
      ?.content.sections.find(({ id }: ISection) => id === targetSectionId)
      ?.rows.find(({ id }: IRow) => id === targetRowId)
      ?.cells.find(({ id }: ICell) => id === targetCellId);

    if (!cell) {
      return [];
    }

    const index = cell.nodes.findIndex(({ id }: INode) => id === targetNodeId);

    if (append) {
      return cell.nodes.slice(index + 1).map(({ id }: INode) => id);
    }

    return cell.nodes.slice(index).map(({ id }: INode) => id);
  }

  static rowContainsCoverImage({ cells }: IRow): boolean {
    const imageCoverNode = (node: IImage) =>
      node.type === ENodeType.Image && node.imageSize === EImageSize.Cover;

    return !!cells.find(({ nodes }: ICell) => !!nodes.find(imageCoverNode));
  }

  static deepMerge(target, source) {
    const isObject = obj => obj && typeof obj === 'object';
    const newTarget = { ...target };
    if (!isObject(newTarget) || !isObject(source)) {
      return source;
    }

    Object.keys(source).forEach(key => {
      const targetValue = newTarget[key];
      const sourceValue = source[key];

      if (Array.isArray(targetValue) && Array.isArray(sourceValue)) {
        newTarget[key] = sourceValue;
      } else if (isObject(targetValue) && isObject(sourceValue)) {
        newTarget[key] = this.deepMerge(Object.assign({}, targetValue), sourceValue);
      } else {
        newTarget[key] = sourceValue;
      }
    });

    return newTarget;
  }

  static deepCopy(src, _visited?, _copiesVisited?) {
    if (src === null || typeof src !== 'object') {
      return src;
    }
    //Honor native/custom clone methods
    if (typeof src.clone == 'function') {
      return src.clone(true);
    }
    if (src instanceof Date) {
      return new Date(src.getTime());
    }
    if (src instanceof RegExp) {
      return new RegExp(src);
    }
    //DOM Element
    if (src.nodeType && typeof src.cloneNode == 'function') {
      return src.cloneNode(true);
    }
    // Initialize the visited objects arrays if needed.
    // This is used to detect cyclic references.
    if (_visited === undefined) {
      _visited = [];
      _copiesVisited = [];
    }
    // Check if this object has already been visited
    for (let index = 0; index < _visited.length; index++) {
      // If so, get the copy we already made
      if (src === _visited[index]) {
        return _copiesVisited[index];
      }
    }
    //Array
    if (Object.prototype.toString.call(src) == '[object Array]') {
      //[].slice() by itself would soft clone
      const ret = src.slice();
      //add it to the visited array
      _visited.push(src);
      _copiesVisited.push(ret);
      let index = ret.length;
      while (index--) {
        ret[index] = this.deepCopy(ret[index], _visited, _copiesVisited);
      }
      return ret;
    }
    //If we've reached here, we have a regular object
    //make sure the returned object has the same prototype as the original
    const proto = Object.getPrototypeOf(src);
    const dest = Object.create(proto);
    //add this object to the visited array
    _visited.push(src);
    _copiesVisited.push(dest);
    for (const key in src) {
      dest[key] = this.deepCopy(src[key], _visited, _copiesVisited);
    }
    return dest;
  }
}
